import React, { useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { FaFileImport, FaPlus, FaTimes } from 'react-icons/fa';
import Modal from '../Modal';
import ResultGrid from './ResultGrid';
import { getActionSelectStyles, getSelectStyles } from '../../utility/selectStyles';
import {
    DefaultKind, DraftColumn, TypeToken, createBody, createProblem, defaultOptions, seedColumns, toSnakeCase, typeOptions,
} from './tableName';
import { Delimiter, DELIMITER_LABEL, CsvShape, detectDelimiter, humanSize, readCsv } from './csv';
import { LoadedCsv, readCsvFile } from './csvFile';
import { draftFromCsv, importColumnsOf, tableNameFromFile } from './fromCsv';
import { UploadOutcome, importFailureLine, uploadProgress } from './importUpload';
import { ImportEnd } from './ImportModal';

/** Everything the New table modal needs to import a CSV into the table it creates (flow 3). */
export interface NewTableImport {
    upload: (bytes: Uint8Array, onProgress: (sent: number, total: number) => void, cancelled: () => boolean) => Promise<UploadOutcome>;
    /** Commit the staged file into a brand-new table, atomically with its creation. */
    commit: (importId: string, sha256: string, delimiter: Delimiter, columns: string[], create: Record<string, unknown>) => Promise<ImportEnd>;
    /** The create and import applied: select the new table (which closes this modal). */
    onSucceeded: (tableName: string) => void;
}

interface NewTableModalProps {
    expert: boolean;
    /** "Creating..." while the write runs; a line when it failed, the draft kept. */
    busy: string | null;
    line: string | null;
    onCreate: (body: Record<string, unknown>) => void;
    onClose: () => void;
    /** When set, a "From CSV" button seeds the draft from a file and the footer creates and imports in one commit. */
    importCsv?: NewTableImport;
    /** A file already chosen (the Tables pane's Import CSV): the draft starts from it, as if From CSV had picked it. */
    initialCsv?: LoadedCsv;
}

/** A chosen file as the draft: its name, its columns and delimiter, or the line saying why it cannot be read. */
const draftOf = (file: LoadedCsv) => {
    if (file.text === null) return { csv: null, line: 'Not UTF-8.', name: '', columns: seedColumns() };
    const delimiter = detectDelimiter(file.text);
    return { csv: { file, delimiter }, line: null, name: tableNameFromFile(file.fileName), columns: draftFromCsv(readCsv(file.text, delimiter, 5)) };
};

const DELIMITERS: Delimiter[] = ['comma', 'semicolon', 'tab', 'pipe'];

/** DESIGN_EDITING.md console flows 2 and 3: the New table modal. The draft lives in this component's state only. */
const NewTableModal: React.FC<NewTableModalProps> = ({ expert, busy, line, onCreate, onClose, importCsv, initialCsv }) => {
    const [initial] = useState(() => (initialCsv ? draftOf(initialCsv) : null));
    const [name, setName] = useState(initial?.name ?? '');
    const [columns, setColumns] = useState<DraftColumn[]>(initial?.columns ?? seedColumns);
    // Flow 3: the chosen file (bytes to upload), its delimiter, and its own write state (upload/commit).
    const [csv, setCsv] = useState<{ file: LoadedCsv; delimiter: Delimiter } | null>(initial?.csv ?? null);
    const [csvBusy, setCsvBusy] = useState<string | null>(null);
    const [csvLine, setCsvLine] = useState<string | null>(initial?.line ?? null);
    const [csvPostgres, setCsvPostgres] = useState<string | null>(null);
    const [csvDone, setCsvDone] = useState(false);
    const [createdName, setCreatedName] = useState('');   // the name the table was made with, fixed once it succeeds
    const fileRef = useRef<HTMLInputElement | null>(null);

    const running = busy !== null || csvBusy !== null;
    const shape: CsvShape | null = useMemo(
        () => (csv && csv.file.text ? readCsv(csv.file.text, csv.delimiter, 5) : null), [csv]);

    const types = typeOptions(expert).map((o) => ({ value: o.token, label: o.label }));
    const setCol = (i: number, patch: Partial<DraftColumn>) =>
        setColumns((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
    const addColumn = () => setColumns((cs) => [...cs, { name: '', type: 'text', required: false, isKey: false, defaultKind: 'none', defaultValue: '' }]);
    const removeColumn = (i: number) => setColumns((cs) => cs.filter((_, j) => j !== i));

    const problem = createProblem(name, columns);

    // ── From CSV (flow 3) ────────────────────────────────────────────────────────────────────────────────────────
    const pickFile = () => { setCsvLine(null); fileRef.current?.click(); };
    const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const draft = draftOf(await readCsvFile(f));
        setCsv(draft.csv);
        setCsvLine(draft.line);
        if (!draft.csv) return;
        setCsvDone(false); setCsvPostgres(null);
        setName(draft.name);
        setColumns(draft.columns);
    };
    const changeDelimiter = (delimiter: Delimiter) => {
        if (!csv || !csv.file.text) return;
        setCsv({ file: csv.file, delimiter });
        setColumns(draftFromCsv(readCsv(csv.file.text, delimiter, 5)));   // a new delimiter re-reads the header
    };

    const createAndImport = async () => {
        if (!csv || !importCsv || problem || running) return;
        const body = createBody('unused', name, columns);   // the commit carries its own write_ref; drop this one
        const { write_ref: _drop, ...create } = body;
        const cols = importColumnsOf(columns);
        if (cols.length === 0) { setCsvLine('The file has no columns to import.'); return; }
        setCsvLine(null); setCsvPostgres(null);
        setCsvBusy(uploadProgress(0, csv.file.size));
        const up = await importCsv.upload(csv.file.bytes, (sent, total) => setCsvBusy(uploadProgress(sent, total)), () => false);
        if (!up.ok) {
            if (up.reason === 'cancelled') { setCsvBusy(null); return; }
            setCsvBusy(null); setCsvLine(importFailureLine(up));
            return;
        }
        setCsvBusy('Importing...');
        const end = await importCsv.commit(up.importId, up.sha256, csv.delimiter, cols, create);
        setCsvBusy(null); setCsvLine(end.line); setCsvPostgres(end.postgres ?? null);
        if (end.ok) { setCreatedName(name); setCsvDone(true); }
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (running || problem) return;
        if (csv) void createAndImport();
        else onCreate(createBody(crypto.randomUUID(), name, columns));
    };

    const footerLabel = csv ? (csvBusy ?? 'Create and import') : (busy ?? 'Create');
    return (
        <Modal isOpen onClose={() => { if (!running) onClose(); }} title="New table" size="lg" closeOnEscape={!running} showCloseButton={!running}>
            <form onSubmit={submit} className="db-data-form db-data-new-table">
                <div className="db-data-new-table-top">
                    <div className="form-group">
                        <label htmlFor="db-new-table-name">Name</label>
                        <input
                            id="db-new-table-name"
                            value={name}
                            placeholder="orders"
                            disabled={running}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => setName((n) => toSnakeCase(n))}
                        />
                    </div>
                    {importCsv && !csvDone && (
                        <>
                            <button type="button" className="db-data-text-button db-data-from-csv" onClick={pickFile} disabled={running}>
                                <FaFileImport aria-hidden="true" /> From CSV
                            </button>
                            <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} onChange={onFileChosen} />
                        </>
                    )}
                </div>

                {csv && (
                    <div className="db-data-import-file-row">
                        <span className="db-data-import-file">{csv.file.fileName} · {humanSize(csv.file.size)}</span>
                        <div className="db-data-import-delimiter">
                            <label id="db-new-delim-label">Delimiter</label>
                            <Select
                                aria-labelledby="db-new-delim-label"
                                options={DELIMITERS.map((d) => ({ value: d, label: DELIMITER_LABEL[d] }))}
                                value={{ value: csv.delimiter, label: DELIMITER_LABEL[csv.delimiter] }}
                                isDisabled={running}
                                isSearchable={false}
                                onChange={(o: any) => changeDelimiter((o?.value ?? 'comma') as Delimiter)}
                                styles={getActionSelectStyles()}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>
                )}

                <div className="db-data-columns" role="group" aria-label="Columns">
                    <div className="db-data-columns-head" aria-hidden="true">
                        <span>Column</span><span>Type</span><span>Default</span><span>Required</span><span>Key</span><span />
                    </div>
                    {columns.map((c, i) => {
                        const defaults = defaultOptions(c.type, { atCreate: true, isKey: c.isKey });
                        const defOpts = defaults.map((o) => ({ value: o.kind, label: o.label }));
                        return (
                            <div className="db-data-column-row" key={i}>
                                <input
                                    className="db-data-col-name"
                                    aria-label={`Column ${i + 1} name`}
                                    value={c.name}
                                    placeholder="name"
                                    disabled={running || c.locked}
                                    onChange={(e) => setCol(i, { name: e.target.value })}
                                    onBlur={(e) => setCol(i, { name: toSnakeCase(e.target.value) })}
                                />
                                <div className="db-data-col-type">
                                    <Select
                                        aria-label={`Column ${i + 1} type`}
                                        options={types}
                                        value={types.find((o) => o.value === c.type) ?? null}
                                        isDisabled={running || c.locked}
                                        onChange={(o: any) => {
                                            const type = (o?.value ?? 'text') as TypeToken;
                                            const allowed = defaultOptions(type, { atCreate: true, isKey: c.isKey }).map((d) => d.kind);
                                            setCol(i, { type, defaultKind: allowed.includes(c.defaultKind) ? c.defaultKind : 'none' });
                                        }}
                                        styles={getActionSelectStyles()}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="db-data-col-default">
                                    <Select
                                        aria-label={`Column ${i + 1} default`}
                                        options={defOpts}
                                        value={defOpts.find((o) => o.value === c.defaultKind) ?? defOpts[0]}
                                        isDisabled={running || c.locked}
                                        onChange={(o: any) => setCol(i, { defaultKind: (o?.value ?? 'none') as DefaultKind })}
                                        styles={getActionSelectStyles()}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {c.defaultKind === 'value' && (
                                        <input
                                            className="db-data-col-default-value"
                                            aria-label={`Column ${i + 1} default value`}
                                            value={c.defaultValue}
                                            placeholder="value"
                                            disabled={running}
                                            onChange={(e) => setCol(i, { defaultValue: e.target.value })}
                                        />
                                    )}
                                </div>
                                <input
                                    type="checkbox"
                                    className="db-data-col-check"
                                    aria-label={`Column ${i + 1} required`}
                                    checked={c.required}
                                    disabled={running || c.locked}
                                    onChange={(e) => setCol(i, { required: e.target.checked })}
                                />
                                <input
                                    type="checkbox"
                                    className="db-data-col-check"
                                    aria-label={`Column ${i + 1} key`}
                                    checked={c.isKey}
                                    disabled={running || c.locked}
                                    onChange={(e) => {
                                        const isKey = e.target.checked;
                                        const allowed = defaultOptions(c.type, { atCreate: true, isKey }).map((d) => d.kind);
                                        setCol(i, { isKey, defaultKind: allowed.includes(c.defaultKind) ? c.defaultKind : 'none' });
                                    }}
                                />
                                <button
                                    type="button"
                                    className="db-data-col-remove"
                                    aria-label={`Remove column ${i + 1}`}
                                    title="Remove column"
                                    disabled={running || c.fromCsv || (c.locked && c.name === 'id')}
                                    onClick={() => removeColumn(i)}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        );
                    })}
                    <button type="button" className="db-data-text-button db-data-add-column" disabled={running} onClick={addColumn}>
                        <FaPlus aria-hidden="true" /> Add column
                    </button>
                </div>

                {shape && shape.header.length > 0 && (
                    <div className="db-data-import-preview">
                        <ResultGrid label={`First rows of ${csv!.file.fileName}`} columns={shape.header} rows={shape.rows} emptyText="No rows." truncatedText={null} />
                    </div>
                )}

                {problem && !running && !csvDone && <p className="db-data-field-line">{problem.line}</p>}
                {(line || csvLine) && <p className="db-data-write-line" role="alert"><span>{line ?? csvLine}</span></p>}
                {expert && csvPostgres && <pre className="db-data-write-postgres">{csvPostgres}</pre>}

                <div className="modal-actions">
                    {csvDone ? (
                        <button type="button" className="submit-button" onClick={() => importCsv?.onSucceeded(createdName)}>Close</button>
                    ) : (
                        <>
                            <button type="button" className="cancel-button" onClick={onClose} disabled={running}>Cancel</button>
                            <button type="submit" className="submit-button" disabled={running || !!problem}>{footerLabel}</button>
                        </>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default NewTableModal;
